/**
 * Products data access + pricing, shared by both apps.
 */
import { publicFetch } from './api'

export type PricingStrategy = 'per_ha' | 'per_ha_capped' | 'flat' | 'free' | 'bundled'

export interface Product {
  id: string
  code: string
  name: string
  description: string | null
  category: string
  price_per_ha: number
  price_min_cents: number | null
  price_max_cents: number | null
  pricing_strategy: PricingStrategy
  resolution: string | null
  format: string | null
  is_active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

/**
 * Calculate the actual price in euros for a product + area, applying
 * the product's pricing strategy and min/max caps.
 * Must match server-side _calculate_price_cents() in orders_routes.py.
 */
export function calculateProductPrice(product: Product, areaHa: number): number {
  if (product.pricing_strategy === 'free' || product.pricing_strategy === 'bundled') {
    return 0
  }

  const rawEur = product.price_per_ha * areaHa
  const rawCents = Math.round(rawEur * 100)

  if (product.pricing_strategy === 'per_ha_capped' || product.pricing_strategy === 'flat') {
    const minC = product.price_min_cents ?? 0
    const maxC = product.price_max_cents ?? Infinity
    return Math.min(Math.max(rawCents, minC), maxC) / 100
  }

  // 'per_ha', no clamping
  return rawCents / 100
}

/**
 * Returns true if the price is at the minimum floor (area too small to
 * affect the price). Useful to show "min. X€" in the UI.
 */
export function isAtMinPrice(product: Product, areaHa: number): boolean {
  if (!product.price_min_cents) return false
  const raw = Math.round(product.price_per_ha * areaHa * 100)
  return raw < product.price_min_cents
}

export const productsService = {
  /**
   * Get all active products (includes bundled OSM context products).
   */
  async getProducts(): Promise<{ products: Product[]; error: Error | null }> {
    const { data, error } = await publicFetch<{ total: number; products: Product[] }>('/products')
    return { products: data?.products ?? [], error }
  },

  /**
   * Get products filtered by category.
   */
  async getProductsByCategory(category: string): Promise<{ products: Product[]; error: Error | null }> {
    const { data, error } = await publicFetch<{ total: number; products: Product[] }>(
      `/products?category=${encodeURIComponent(category)}`
    )
    return { products: data?.products ?? [], error }
  },

  /**
   * Get a single product by code.
   */
  async getProductByCode(code: string): Promise<{ product: Product | null; error: Error | null }> {
    const { data, error } = await publicFetch<Product>(`/products/${encodeURIComponent(code)}`)
    return { product: data, error }
  },
}
